import React, { useState, useEffect } from "react";
import { Plus, Trash2, Edit2, Save, X, Apple } from "lucide-react";
import { toast } from "sonner";
import { API } from "./SharedComponents";

const GOAL_LABELS = { cut: "Cut", maintain: "Maintain", bulk: "Bulk" };
const GOAL_COLORS = { cut: "text-red-400", maintain: "text-blue-400", bulk: "text-emerald-400" };
const DEFAULT_GOALS = {
  cut: { protein_per_lb: 1.2, carbs_per_lb: 0.8, fat_per_lb: 0.3 },
  maintain: { protein_per_lb: 1.0, carbs_per_lb: 1.2, fat_per_lb: 0.4 },
  bulk: { protein_per_lb: 1.0, carbs_per_lb: 1.8, fat_per_lb: 0.5 },
};

const MacroRow = ({ label, field, goals, setGoals, goalKey }) => (
  <div className="flex items-center gap-3">
    <span className="text-sm text-gray-400 w-24">{label}</span>
    <input
      type="number" step="0.05" min="0"
      value={goals[goalKey]?.[field] || ""}
      onChange={(e) => setGoals({ ...goals, [goalKey]: { ...goals[goalKey], [field]: parseFloat(e.target.value) || 0 } })}
      className="w-24 bg-[#0B0F1A] border border-[#1E293B] rounded px-2 py-1.5 text-sm text-white text-center focus:border-emerald-500 outline-none"
      data-testid={`macro-${goalKey}-${field}`}
    />
    <span className="text-xs text-gray-500">g/lb</span>
  </div>
);

const NutritionBuilder = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", goals: { ...DEFAULT_GOALS } });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/nutrition/plans`, { credentials: "include" });
      if (res.ok) { const data = await res.json(); setPlans(data.plans || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error("Plan name is required"); return; }
    try {
      const url = editingPlan ? `${API}/nutrition/plans/${editingPlan.plan_id}` : `${API}/nutrition/plans`;
      const method = editingPlan ? "PUT" : "POST";
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success(editingPlan ? "Plan updated" : "Plan created");
        fetchPlans();
        resetForm();
      }
    } catch (e) { toast.error("Failed to save plan"); }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Delete this nutrition plan?")) return;
    try {
      const res = await fetch(`${API}/nutrition/plans/${planId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Plan deleted"); fetchPlans(); }
    } catch (e) { toast.error("Failed to delete"); }
  };

  const startEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({ name: plan.name, description: plan.description || "", goals: plan.goals || { ...DEFAULT_GOALS } });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({ name: "", description: "", goals: { ...DEFAULT_GOALS } });
    setShowForm(false);
  };

  // Preview calories for a sample 180lb athlete
  const previewCalories = (goals, goalKey) => {
    const g = goals[goalKey] || {};
    const w = 180;
    const p = w * (g.protein_per_lb || 0);
    const c = w * (g.carbs_per_lb || 0);
    const f = w * (g.fat_per_lb || 0);
    return Math.round(p * 4 + c * 4 + f * 9);
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Nutrition Plans</h2>
          <p className="text-sm text-gray-500 mt-1">Define macro formulas per lb of bodyweight for each goal</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-testid="create-nutrition-plan">
            <Plus className="w-4 h-4" /> New Plan
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editingPlan ? "Edit Plan" : "Create Plan"}</h3>
            <button onClick={resetForm} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Plan Name</label>
              <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 outline-none" placeholder="e.g. Standard Macros" data-testid="nutrition-plan-name" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Description (optional)</label>
              <input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 outline-none" placeholder="Brief description" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["cut", "maintain", "bulk"].map(goalKey => (
              <div key={goalKey} className="bg-[#0B0F1A] border border-[#1E293B] rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className={`font-semibold ${GOAL_COLORS[goalKey]}`}>{GOAL_LABELS[goalKey]}</h4>
                  <span className="text-[10px] text-gray-600">~{previewCalories(formData.goals, goalKey)} cal @180lb</span>
                </div>
                <MacroRow label="Protein" field="protein_per_lb" goals={formData.goals} setGoals={(g) => setFormData({ ...formData, goals: g })} goalKey={goalKey} />
                <MacroRow label="Carbs" field="carbs_per_lb" goals={formData.goals} setGoals={(g) => setFormData({ ...formData, goals: g })} goalKey={goalKey} />
                <MacroRow label="Fat" field="fat_per_lb" goals={formData.goals} setGoals={(g) => setFormData({ ...formData, goals: g })} goalKey={goalKey} />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button onClick={handleSave} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded-lg text-sm font-medium" data-testid="save-nutrition-plan">
              <Save className="w-4 h-4" /> {editingPlan ? "Update" : "Create"} Plan
            </button>
          </div>
        </div>
      )}

      {/* Existing Plans */}
      <div className="space-y-3">
        {plans.map(plan => (
          <div key={plan.plan_id} className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-4" data-testid={`nutrition-plan-${plan.plan_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-semibold flex items-center gap-2"><Apple className="w-4 h-4 text-emerald-400" /> {plan.name}</h4>
                {plan.description && <p className="text-xs text-gray-500 mt-1">{plan.description}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(plan)} className="p-2 text-gray-500 hover:text-white hover:bg-[#1E293B] rounded-lg" data-testid={`edit-plan-${plan.plan_id}`}><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(plan.plan_id)} className="p-2 text-gray-500 hover:text-red-400 hover:bg-[#1E293B] rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["cut", "maintain", "bulk"].map(goalKey => {
                const g = plan.goals?.[goalKey] || {};
                return (
                  <div key={goalKey} className="bg-[#0B0F1A] rounded-lg p-3 text-center">
                    <p className={`text-xs font-semibold mb-1 ${GOAL_COLORS[goalKey]}`}>{GOAL_LABELS[goalKey]}</p>
                    <p className="text-[10px] text-gray-500">P: {g.protein_per_lb || 0} • C: {g.carbs_per_lb || 0} • F: {g.fat_per_lb || 0}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {plans.length === 0 && !showForm && (
          <div className="text-center py-12 text-gray-600">No nutrition plans yet. Create one to get started.</div>
        )}
      </div>
    </div>
  );
};

export default NutritionBuilder;
